import React, { useEffect, useRef, useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import api, { apiFetch } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, SendHorizontal, BookmarkPlus, Check } from "lucide-react";
import { toast } from "sonner";
import ProtocolCard from "@/components/ProtocolCard";

export default function Concierge() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [streaming, setStreaming] = useState(false);
  const [summaries, setSummaries] = useState({}); // { [assistantId]: { loading, data, question, saved, saving } }
  const scrollRef = useRef(null);

  const summarize = async (aId, question, answer) => {
    setSummaries((s) => ({ ...s, [aId]: { loading: true, question } }));
    try {
      const r = await api.post("/concierge/summarize", { answer, question });
      setSummaries((s) => ({ ...s, [aId]: { loading: false, data: r.data, question, saved: false } }));
    } catch (e) {
      setSummaries((s) => ({ ...s, [aId]: { loading: false, error: true, question } }));
    }
  };

  const saveProtocol = async (aId) => {
    const entry = summaries[aId];
    if (!entry?.data) return;
    setSummaries((s) => ({ ...s, [aId]: { ...s[aId], saving: true } }));
    try {
      const title = (entry.question || entry.data.takeaways?.[0] || "Saved protocol").slice(0, 120);
      await api.post("/my-protocols", { title, source_question: entry.question, ...entry.data });
      setSummaries((s) => ({ ...s, [aId]: { ...s[aId], saved: true, saving: false } }));
      toast.success("Saved to your protocol — view it on your dashboard.");
    } catch (e) {
      setSummaries((s) => ({ ...s, [aId]: { ...s[aId], saving: false } }));
      toast.error(e?.response?.data?.detail || "Could not save");
    }
  };

  useEffect(() => {
    api.get("/concierge/history").then((r) => {
      const msgs = r.data || [];
      setMessages(msgs);
      if (msgs.length > 0) setSessionId(msgs[msgs.length - 1].session_id);
    });
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const send = async (e) => {
    e?.preventDefault();
    if (!input.trim() || streaming) return;
    const userMsg = input.trim();
    setInput("");
    setMessages((m) => [...m, { role: "user", content: userMsg, id: `u-${Date.now()}` }]);
    setStreaming(true);

    const aId = `a-${Date.now()}`;
    setMessages((m) => [...m, { role: "assistant", content: "", id: aId }]);

    try {
      const resp = await apiFetch("/concierge/chat", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ message: userMsg, session_id: sessionId }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ detail: "Concierge unavailable" }));
        setMessages((m) => m.map((x) => x.id === aId ? { ...x, content: `[error] ${err.detail || "Stream error"}` } : x));
        setStreaming(false);
        return;
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let full = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const p of parts) {
          const line = p.startsWith("data: ") ? p.slice(6) : p;
          if (!line.trim()) continue;
          try {
            const obj = JSON.parse(line);
            if (obj.delta) {
              full += obj.delta;
              setMessages((m) => m.map((x) => x.id === aId ? { ...x, content: x.content + obj.delta } : x));
            } else if (obj.session_id) {
              setSessionId(obj.session_id);
            } else if (obj.error) {
              setMessages((m) => m.map((x) => x.id === aId ? { ...x, content: `[error] ${obj.error}` } : x));
            }
          } catch (e) { console.debug("Skipping non-JSON SSE chunk", e); }
        }
      }
      if (full.trim() && !full.startsWith("[error]")) {
        summarize(aId, userMsg, full);
      }
    } catch (e) {
      setMessages((m) => m.map((x) => x.id === aId ? { ...x, content: "[error] connection lost" } : x));
    } finally {
      setStreaming(false);
    }
  };

  const starters = [
    "I have high cortisol mornings — Ayurveda and labs perspectives?",
    "What plant medicines support deep sleep, and what biomarkers can I track?",
    "Help me design a 12-week longevity protocol.",
  ];

  return (
    <div className="min-h-screen bg-[#F9F6F0] flex flex-col">
      <Header />
      <section className="flex-1 px-6 sm:px-12 lg:px-20 pt-12 pb-16 max-w-4xl mx-auto w-full">
        <div className="flex items-center gap-3 mb-2">
          <Sparkles className="w-5 h-5 text-[#D49A44]" />
          <p className="label-eyebrow">AI Concierge · Massey</p>
        </div>
        <h1 className="font-serif text-4xl text-[#2D322B] mb-2">Ask the keeper.</h1>
        <p className="text-sm text-[#5C6359] mb-8">Both lab science and ancestral wisdom answer.</p>

        <div ref={scrollRef} className="bg-[#F1ECE4] border border-[#D1C7B8] rounded-3xl p-6 h-[480px] overflow-y-auto" data-testid="concierge-thread">
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center" data-testid="empty-thread">
              <p className="text-[#5C6359] mb-6">Begin with a question, or try:</p>
              <div className="grid gap-3 w-full max-w-md">
                {starters.map((s, i) => (
                  <button key={s} onClick={() => setInput(s)} data-testid={`starter-${i}`} className="text-left text-sm text-[#2D322B] bg-[#F9F6F0] border border-[#D1C7B8] hover:border-[#C05C42] rounded-2xl px-4 py-3 transition-colors">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {messages.map((m) => (
                <div key={m.id} data-testid={`msg-${m.role}`}>
                  <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] rounded-2xl px-5 py-3 ${m.role === "user" ? "bg-[#2D322B] text-[#F9F6F0]" : "bg-[#F9F6F0] text-[#2D322B] border border-[#D1C7B8]"}`}>
                      {m.role === "assistant" && <p className="label-eyebrow !text-[#C05C42] mb-1">Massey</p>}
                      <p className="text-sm leading-relaxed whitespace-pre-wrap">{m.content || (streaming ? "…" : "")}</p>
                    </div>
                  </div>
                  {m.role === "assistant" && summaries[m.id] && (
                    <div className="mt-3 max-w-[92%]">
                      {summaries[m.id].loading ? (
                        <p className="text-xs text-[#5C6359] flex items-center gap-2" data-testid="summary-loading">
                          <Sparkles className="w-3.5 h-3.5 text-[#D49A44] animate-pulse" /> Massey is synthesising your protocol…
                        </p>
                      ) : summaries[m.id].data ? (
                        <ProtocolCard
                          protocol={summaries[m.id].data}
                          testid={`summary-card-${m.id}`}
                          footer={
                            <button
                              onClick={() => saveProtocol(m.id)}
                              disabled={summaries[m.id].saved || summaries[m.id].saving}
                              data-testid={`save-protocol-${m.id}`}
                              className="btn-primary !py-2 !px-5 !text-xs inline-flex items-center gap-2 disabled:opacity-70"
                            >
                              {summaries[m.id].saved
                                ? <><Check className="w-4 h-4" /> Saved to dashboard</>
                                : <><BookmarkPlus className="w-4 h-4" /> {summaries[m.id].saving ? "Saving…" : "Save to my protocol"}</>}
                            </button>
                          }
                        />
                      ) : null}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <form onSubmit={send} className="mt-4 flex gap-3" data-testid="concierge-form">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask about a protocol, plant, or biomarker…"
            data-testid="concierge-input"
            className="flex-1 bg-[#F1ECE4] border border-[#D1C7B8] focus:border-[#C05C42] rounded-full px-6 py-3 outline-none text-[#2D322B]"
          />
          <button type="submit" disabled={streaming || !input.trim()} data-testid="concierge-send" className="btn-primary !px-5 inline-flex items-center gap-2">
            {streaming ? "…" : <><SendHorizontal className="w-4 h-4" /> Send</>}
          </button>
        </form>
      </section>
      <Footer />
    </div>
  );
}
